/**
 * Edit Payment Method Modal Component
 * 
 * Allows users to update their payment method for existing subscriptions.
 * Uses Stripe Elements for secure card input.
 */

import React, { useState, useEffect } from 'react';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import {
  StripeCardNumberElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardCvcElementChangeEvent,
} from '@stripe/stripe-js';
import { useAppDispatch } from '../../../store';
import { showToast, closeModal } from '../../../store/slices/uiSlice';
import { setPaymentMethod } from '../../../store/slices/paymentSlice';
import {
  paymentSetUpIntentSubscription,
  updatePaymentMethod,
} from '../../../api/paymentApi';
import styles from './EditPaymentMethodModal.module.scss';

// Icons
import CvcIcon from '../../../assets/icons/cvc.svg';
import StripeIcon from '../../../assets/icons/stripe.svg';
import LoadingIcon from '../../../assets/icons/loading.svg';
import CloseIcon from '../../../assets/icons/close.svg';

// ============================================
// Types
// ============================================

/** Union type for all card element change events */
type StripeCardElementChangeEvent =
  | StripeCardNumberElementChangeEvent
  | StripeCardExpiryElementChangeEvent
  | StripeCardCvcElementChangeEvent;

// ============================================
// Constants
// ============================================

/** Stripe Element styling options */
const CARD_ELEMENT_OPTIONS = {
  showIcon: true,
  style: {
    base: {
      fontSize: '14px',
      lineHeight: '22px',
      fontWeight: '400',
      fontSmoothing: 'antialiased',
      iconColor: '#666',
      '::placeholder': {
        color: '#A29DB0',
      },
    },
    invalid: {
      color: '#F14055',
      borderColor: '#F14055',
    },
  },
};

// ============================================
// Types
// ============================================

interface CardErrors {
  numberErr: string;
  cvcErr: string;
  expDateErr: string;
  stripeErrorMessage: string;
}

interface CardTouched {
  numberTouched: boolean;
  expDateTouched: boolean;
  cvcTouched: boolean;
  nameTouched: boolean;
}

interface EditPaymentMethodModalProps {
  /** Callback fired after successful payment method update */
  onSuccess?: () => void;
}

// ============================================
// Component
// ============================================

const EditPaymentMethodModal: React.FC<EditPaymentMethodModalProps> = ({ onSuccess }) => {
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const elements = useElements();

  // Local state
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [cardholderName, setCardholderName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stripeErrorConfirm, setStripeErrorConfirm] = useState('');

  const [errors, setErrors] = useState<CardErrors>({
    numberErr: '',
    cvcErr: '',
    expDateErr: '',
    stripeErrorMessage: '',
  });

  const [touched, setTouched] = useState<CardTouched>({
    numberTouched: false,
    expDateTouched: false,
    cvcTouched: false,
    nameTouched: false,
  });

  // Check if form is valid for submission
  const isFormValid =
    !errors.numberErr &&
    !errors.cvcErr &&
    !errors.expDateErr &&
    touched.numberTouched &&
    touched.expDateTouched &&
    touched.cvcTouched &&
    touched.nameTouched;

  // ============================================
  // Effects
  // ============================================

  // Fetch setup intent on mount
  useEffect(() => {
    const fetchSetupIntent = async () => {
      try {
        const data = await paymentSetUpIntentSubscription(true);
        setClientSecret(data.token);
      } catch (error) {
        console.error('Failed to fetch setup intent:', error);
        dispatch(
          showToast({
            type: 'error',
            body: 'Fehler beim Laden. Bitte versuchen Sie es erneut.',
          })
        );
      }
    };

    fetchSetupIntent();
  }, [dispatch]);

  // ============================================
  // Handlers
  // ============================================

  /** Close the modal */
  const handleClose = () => {
    dispatch(closeModal());
  };

  /** Handle cardholder name input */
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCardholderName(value);
    setTouched((prev) => ({
      ...prev,
      nameTouched: value.trim().length > 0,
    }));
  };

  /** Handle Stripe element change events */
  const handleCardElementChange = (
    event: StripeCardElementChangeEvent,
    field: 'number' | 'expDate' | 'cvc'
  ) => {
    setStripeErrorConfirm('');

    const fieldKey = `${field}Touched` as keyof CardTouched;
    const errorKey = `${field}Err` as keyof CardErrors;

    setTouched((prev) => ({
      ...prev,
      [fieldKey]: event.complete,
    }));

    if (event.error?.message) {
      setErrors((prev) => ({
        ...prev,
        [errorKey]: event.error?.message || '',
        stripeErrorMessage: event.error?.message || '',
      }));
    } else {
      setErrors((prev) => {
        const updatedErrors = { ...prev, [errorKey]: '' };
        updatedErrors.stripeErrorMessage =
          updatedErrors.numberErr || updatedErrors.expDateErr || updatedErrors.cvcErr || '';
        return updatedErrors;
      });
    }
  };

  /** Handle form submission */
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      dispatch(
        showToast({
          type: 'error',
          body: 'Stripe wurde nicht korrekt geladen.',
        })
      );
      return;
    }

    if (!isFormValid) {
      return;
    }

    setIsSubmitting(true);
    setStripeErrorConfirm('');

    try {
      const cardElement = elements.getElement(CardNumberElement);

      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Confirm card setup with Stripe
      const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: cardholderName,
          },
        },
      });

      if (error) {
        setStripeErrorConfirm('Die Zahlungsmethode konnte nicht verifiziert werden.');
        setIsSubmitting(false);
        return;
      }

      if (!setupIntent?.payment_method) {
        throw new Error('No payment method returned');
      }

      const paymentMethodId =
        typeof setupIntent.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent.payment_method.id;

      // Update payment method on server
      await updatePaymentMethod({ paymentMethodId });

      // Store payment method in Redux
      dispatch(setPaymentMethod(paymentMethodId));

      dispatch(
        showToast({
          type: 'success',
          body: 'Zahlungsmethode erfolgreich aktualisiert.',
        })
      );

      dispatch(closeModal());
      onSuccess?.();
    } catch (error) {
      console.error('Update payment method error:', error);
      setStripeErrorConfirm('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================
  // Render
  // ============================================

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-payment-title"
      >
        <div className={styles.header}>
          <h2 id="edit-payment-title">Zahlungsmethode aktualisieren</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Schließen"
          >
            <img src={CloseIcon} alt="" aria-hidden="true" />
          </button>
        </div>

        <form className={styles.form} onSubmit={handleSave}>
          {/* Card Number */}
          <div className={styles.fieldGroup}>
            <label>
              <span>Kartendaten</span>
              <div
                className={`${styles.cardElementWrapper} ${
                  errors.numberErr ? styles.invalidElementWrapper : ''
                }`}
              >
                <CardNumberElement
                  options={CARD_ELEMENT_OPTIONS}
                  className={`${styles.cardElement} ${errors.numberErr ? styles.invalidElement : ''}`}
                  onChange={(e) => handleCardElementChange(e, 'number')}
                />
              </div>

              <div className={styles.cardDetailsRow}>
                <div
                  className={`${styles.expiryWrapper} ${
                    errors.expDateErr ? styles.invalidElementWrapper : ''
                  }`}
                >
                  <CardExpiryElement
                    options={CARD_ELEMENT_OPTIONS}
                    className={`${styles.expiredElement} ${
                      errors.expDateErr ? styles.invalidElement : ''
                    }`}
                    onChange={(e) => handleCardElementChange(e, 'expDate')}
                  />
                </div>
                <div
                  className={`${styles.cvcWrapper} ${
                    errors.cvcErr ? styles.invalidElementWrapper : ''
                  }`}
                >
                  <img src={CvcIcon} alt="" className={styles.cvcIcon} aria-hidden="true" />
                  <CardCvcElement
                    options={CARD_ELEMENT_OPTIONS}
                    className={`${styles.cvcElement} ${errors.cvcErr ? styles.invalidElement : ''}`}
                    onChange={(e) => handleCardElementChange(e, 'cvc')}
                  />
                </div>
              </div>

              {errors.stripeErrorMessage && (
                <span className={styles.errorMessage} role="alert">
                  {errors.stripeErrorMessage}
                </span>
              )}
            </label>
          </div>

          {/* Cardholder Name */}
          <div className={styles.fieldGroup}>
            <label>
              <span>Name des/der Karteninhaber/in</span>
              <input
                type="text"
                value={cardholderName}
                onChange={handleNameChange}
                placeholder="Vollständiger Name"
                aria-label="Name des Karteninhabers"
              />
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className={`${styles.submitButton} ${isSubmitting ? styles.disabled : ''}`}
            disabled={isSubmitting || !isFormValid}
            aria-busy={isSubmitting}
          >
            Speichern
            {isSubmitting && (
              <img src={LoadingIcon} alt="" className={styles.spinner} aria-hidden="true" />
            )}
          </button>

          {/* Stripe Error */}
          {stripeErrorConfirm && (
            <span className={styles.errorMessageStripe} role="alert">
              {stripeErrorConfirm}
            </span>
          )}

          {/* Stripe Branding */}
          <div className={styles.stripeInfo}>
            <span>Powered by</span>
            <img src={StripeIcon} alt="Stripe" />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPaymentMethodModal;
